/**
 * One walk over `BALANCE_SHEET_LAYOUT`, shared by every rendering of the
 * balance sheet.
 *
 * The rule that keeps drifting is procedural, not declarative: equity renders
 * when the ledger carries an EQUITY section OR when either derived figure is
 * non-zero, and a young ledger with posted revenue hits the second limb with
 * no section to hang a header on. A layout walked twice would state that rule
 * twice, which is how the series came to emit two rows labelled "Total equity"
 * carrying different figures. So the walk lives here once, and each projection
 * supplies only how to FIND a section and how to READ a figure.
 *
 * The two type parameters are what let one walk serve both shapes. A
 * single-date rendering carries one figure per row (`TAmount = number`) and a
 * concrete section; a series carries one figure per column
 * (`TAmount = number[]`) over a merged section. Nothing in the walk cares
 * which - it never adds two figures without asking the caller's `sum`, and
 * never tests one for zero without asking the caller's `isZero`.
 */
import { BALANCE_SHEET_LAYOUT, } from './ledger.js';
/**
 * A stable identity for one projected row, shared by every renderer that keys
 * React rows or an xlsx/pdf write cursor off a `BalanceSheetLayoutRow`. Kind,
 * section key (or the `footer` sentinel for a `total` row, which carries no
 * section key of its own) and label together are unique across a statement -
 * a section appears once as a header and once as its own subtotal, and no two
 * rows of one kind otherwise share both a key and a label.
 *
 * The one exception is `derived`: both equity lines share `key: 'EQUITY'`, so
 * only the label tells them apart - and that label is partly user-controlled
 * (`labelFor('accumulatedProfitAccountName')` returns the ledger's own account
 * name). A ledger whose ACCUMULATED_PROFIT account happened to be named
 * exactly "Current year earnings" would collide with the layout's own label
 * for the other derived line. `field` is the underlying `BalanceSheetDerivedField`
 * the row was computed from, which the layout controls and never a ledger's
 * account name, so a `derived` row keys on that instead.
 */
export function balanceSheetRowKey(row) {
    if (row.kind === 'derived')
        return `derived-${row.field}`;
    return `${row.kind}-${row.kind === 'total' ? 'footer' : row.key}-${row.label}`;
}
/**
 * Walks `BALANCE_SHEET_LAYOUT` into rows.
 *
 * `body` and `footer` are kept apart rather than flattened because the screen
 * renders the footer sums in a `<tfoot>`; a flat array would leave it guessing
 * which trailing rows belong there. Projections that want one list concatenate.
 */
export function projectBalanceSheetLayout(input) {
    const body = [];
    for (const slot of BALANCE_SHEET_LAYOUT.body) {
        const found = input.sectionFor(slot.key);
        if (slot.kind === 'section') {
            if (!found)
                continue;
            body.push({ kind: 'section', key: slot.key, label: found.label, section: found.section });
            body.push({
                kind: 'subtotal',
                key: slot.key,
                label: `Total ${found.label.toLowerCase()}`,
                amountMinorUnits: found.subtotalMinorUnits,
            });
            continue;
        }
        // Equity exists on the statement when the ledger has the section, or when
        // either derived figure is non-zero: a young ledger with posted revenue
        // carries earnings with no equity account to hold them, and a statement
        // that omitted them would not balance.
        const derivedAmounts = slot.derived.map((line) => input.field(line.field));
        if (!found && derivedAmounts.every((amount) => input.isZero(amount)))
            continue;
        body.push({ kind: 'section', key: slot.key, label: found?.label ?? slot.label, section: found?.section ?? null });
        slot.derived.forEach((line, index) => {
            body.push({
                kind: 'derived',
                key: slot.key,
                field: line.field,
                label: (line.labelFrom ? input.labelFor(line.labelFrom) : null) ?? line.label,
                amountMinorUnits: derivedAmounts[index],
            });
        });
        body.push({
            kind: 'subtotal',
            key: slot.key,
            label: slot.subtotal.label,
            amountMinorUnits: input.field(slot.subtotal.field),
        });
    }
    const footer = BALANCE_SHEET_LAYOUT.footer.map((slot) => ({
        kind: 'total',
        label: slot.label,
        amountMinorUnits: input.sum(slot.fields.map((fieldName) => input.field(fieldName))),
        sections: slot.sections,
    }));
    return { body, footer };
}
/** The single-date projection: one figure per row, sections as the response carries them. */
export function buildBalanceSheetTableRows(figures) {
    return projectBalanceSheetLayout({
        sectionFor: (key) => {
            const section = figures.sections.find((candidate) => candidate.section === key);
            return section ? { section, label: section.label, subtotalMinorUnits: section.subtotalMinorUnits } : undefined;
        },
        field: (name) => figures[name],
        labelFor: () => figures.accumulatedProfitAccountName,
        sum: (amounts) => amounts.reduce((total, amount) => total + amount, 0),
        isZero: (amount) => amount === 0,
    });
}
